import store from '@/store/BookingStore';

const STORAGE_KEY = 'bookingFunnelUrlTokenMap';
const VALID_DAYS_IN_LOCAL_STORAGE = 30;

export interface UrlTokenEntry {
    token: string;
    creationDate: string;
}

export type UrlTokenMap = Record<string, UrlTokenEntry>;

const daysBetweenDates = (creationDate: string): number =>
    (Date.now() - new Date(creationDate).getTime()) / (1000 * 60 * 60 * 24);

const isEntryValid = (entry: UrlTokenEntry): boolean =>
    daysBetweenDates(entry.creationDate) <= VALID_DAYS_IN_LOCAL_STORAGE;

const readMap = (): UrlTokenMap => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        return JSON.parse(raw) as UrlTokenMap;
    } catch (error) {
        console.error('[bookingFunnelUrlTokenCache] failed to read map', error);
        return {};
    }
};

const pruneExpiredEntries = (map: UrlTokenMap): UrlTokenMap => {
    const pruned: UrlTokenMap = {};
    for (const [url, entry] of Object.entries(map)) {
        if (entry?.token && entry.creationDate && isEntryValid(entry)) {
            pruned[url] = entry;
        }
    }
    return pruned;
};

const writeMap = (map: UrlTokenMap): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (error) {
        console.error('[bookingFunnelUrlTokenCache] failed to write map', error);
    }
};

export const lookupToken = (url: string): string | undefined => {
    const map = pruneExpiredEntries(readMap());
    writeMap(map);

    const entry = map[url];
    if (!entry || !isEntryValid(entry)) {
        return undefined;
    }
    return entry.token;
};

export const saveUrlTokenMapping = (url: string, token: string): void => {
    if (!url || !token) return;

    const map = pruneExpiredEntries(readMap());
    map[url] = {
        token,
        creationDate: new Date().toJSON(),
    };
    writeMap(map);
};

export const removeUrlTokenMapping = (url: string): void => {
    if (!url) return;

    const map = readMap();
    if (!(url in map)) return;

    delete map[url];
    writeMap(map);
};

export const removeUrlTokenMappingsForToken = (token: string): void => {
    if (!token) return;

    const map = readMap();
    let changed = false;

    for (const [url, entry] of Object.entries(map)) {
        if (entry?.token === token) {
            delete map[url];
            changed = true;
        }
    }

    if (changed) {
        writeMap(map);
    }
};

export const tryRestoreTokenFromUrlCache = (): string | undefined => {
    const token = lookupToken(window.location.href);
    if (!token) return undefined;

    store.commit('setToken', token);
    return token;
};
